import React from 'react';

type SonnerModule = typeof import('sonner');

let sonnerModule: SonnerModule | null = null;
let sonnerPromise: Promise<SonnerModule> | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => {
    listener();
  });
}

export function subscribeSonnerLoaded(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function loadSonner(): Promise<SonnerModule> {
  if (sonnerModule) {
    return sonnerModule;
  }
  if (!sonnerPromise) {
    sonnerPromise = import('sonner').then((mod) => {
      sonnerModule = mod as SonnerModule;
      notifyListeners();
      return sonnerModule;
    });
  }
  return sonnerPromise;
}

type ToastArgs = [message: string, ...rest: any[]];

function callToast(method: 'success' | 'error' | 'warning' | 'info', ...args: ToastArgs): void {
  void loadSonner().then((mod) => {
    const anyToast: any = (mod as any).toast;
    const fn: any = anyToast[method] ?? anyToast;
    if (typeof fn === 'function') {
      fn(...args);
    }
  });
}

export const toast = {
  success: (...args: ToastArgs) => callToast('success', ...args),
  error: (...args: ToastArgs) => callToast('error', ...args),
  warning: (...args: ToastArgs) => callToast('warning', ...args),
  info: (...args: ToastArgs) => callToast('info', ...args),
};

export function LazyToaster(props: any) {
  const [, setTick] = React.useState(0);

  React.useEffect(() => {
    void loadSonner();

    const unsubscribe = subscribeSonnerLoaded(() => {
      setTick((value) => value + 1);
    });
    return unsubscribe;
  }, []);

  if (!sonnerModule) {
    return null;
  }

  const Toaster = (sonnerModule as any).Toaster as React.ComponentType<any>;
  return <Toaster {...props} />;
}
